import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  InvoicePayment,
  PaymentWithDetails,
  RecordPaymentInput,
  InvoiceWithPayments,
} from '@/types/payments';

// =====================================================
// QUERIES
// =====================================================

/**
 * Get all payments for an invoice
 */
export function useInvoicePayments(invoiceId: string) {
  return useQuery({
    queryKey: ['invoice-payments', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_payments')
        .select(`
          *,
          transaction:transactions(
            id,
            transaction_number,
            payment_method,
            check_number,
            check_status,
            account_id
          )
        `)
        .eq('invoice_id', invoiceId)
        .is('deleted_at', null)
        .order('payment_date', { ascending: false });

      if (error) throw error;
      return (data as PaymentWithDetails[]) || [];
    },
    enabled: !!invoiceId,
  });
}

/**
 * Get all payments for a company
 */
export function useCompanyPayments(companyId: string, filters?: {
  dateFrom?: string;
  dateTo?: string;
  paymentMethod?: string;
}) {
  return useQuery({
    queryKey: ['company-payments', companyId, filters],
    queryFn: async () => {
      let query = supabase
        .from('invoice_payments')
        .select(`
          *,
          transaction:transactions(*),
          invoice:invoices(id, invoice_number, total, client_name)
        `)
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('payment_date', { ascending: false });

      if (filters?.dateFrom) {
        query = query.gte('payment_date', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('payment_date', filters.dateTo);
      }

      if (filters?.paymentMethod) {
        query = query.eq('payment_method', filters.paymentMethod);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data as PaymentWithDetails[]) || [];
    },
    enabled: !!companyId,
  });
}

/**
 * Get invoice with payment details
 */
export function useInvoiceWithPayments(invoiceId: string) {
  return useQuery({
    queryKey: ['invoice-with-payments', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          payments:invoice_payments(
            *,
            transaction:transactions(*)
          )
        `)
        .eq('id', invoiceId)
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      return data as InvoiceWithPayments;
    },
    enabled: !!invoiceId,
  });
}

/**
 * Get outstanding invoices (unpaid + partial)
 */
export function useOutstandingInvoices(companyId: string, filters?: {
  clientId?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: 'unpaid' | 'partial' | 'all';
}) {
  return useQuery({
    queryKey: ['outstanding-invoices', companyId, filters],
    queryFn: async () => {
      let query = supabase
        .from('invoices')
        .select('*')
        .eq('company_id', companyId)
        .in('payment_status', filters?.status === 'all' 
          ? ['unpaid', 'partial'] 
          : [filters?.status || 'unpaid', 'partial']
        )
        .is('deleted_at', null)
        .order('due_date', { ascending: true });

      if (filters?.clientId) {
        query = query.eq('client_id', filters.clientId);
      }

      if (filters?.dateFrom) {
        query = query.gte('invoice_date', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('invoice_date', filters.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data as InvoiceWithPayments[]) || [];
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Get AR aging stats
 */
export function useARAgingStats(companyId: string) {
  return useQuery({
    queryKey: ['ar-aging-stats', companyId],
    queryFn: async () => {
      const { data: invoices, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('company_id', companyId)
        .in('payment_status', ['unpaid', 'partial'])
        .is('deleted_at', null);

      if (error) throw error;

      const today = new Date();
      const stats = {
        total: 0,
        current: 0, // Not overdue
        days_1_30: 0,
        days_31_60: 0,
        days_61_90: 0,
        days_90_plus: 0,
      };

      (invoices || []).forEach((invoice: any) => {
        const outstanding = invoice.amount_outstanding || 0;
        stats.total += outstanding;

        const dueDate = new Date(invoice.due_date);
        const daysOverdue = Math.floor(
          (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (daysOverdue <= 0) {
          stats.current += outstanding;
        } else if (daysOverdue <= 30) {
          stats.days_1_30 += outstanding;
        } else if (daysOverdue <= 60) {
          stats.days_31_60 += outstanding;
        } else if (daysOverdue <= 90) {
          stats.days_61_90 += outstanding;
        } else {
          stats.days_90_plus += outstanding;
        }
      });

      return stats;
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// =====================================================
// MUTATIONS
// =====================================================

/**
 * Record a payment for an invoice
 */
export function useRecordPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RecordPaymentInput & { companyId: string; userId: string }) => {
      // 1. Create the transaction
      const transactionData: any = {
        company_id: input.companyId,
        account_id: input.account_id,
        type: 'income',
        amount: input.amount,
        payment_method: input.payment_method,
        transaction_date: input.payment_date,
        transaction_number: `PAY-${Date.now()}`,
        currency: 'DZD',
        description: input.description || `Payment for invoice`,
        notes: input.notes,
        created_by: input.userId,
        is_reconciled: false,
        reference_type: 'invoice',
        reference_id: input.invoice_id,
      };

      // Add check fields if payment method is check
      if (input.payment_method === 'check') {
        transactionData.check_number = input.check_number;
        transactionData.check_date = input.check_date || input.payment_date;
        transactionData.check_bank = input.check_bank;
        transactionData.check_drawer_name = input.check_drawer_name;
        transactionData.check_status = 'pending';
      }

      const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .insert(transactionData)
        .select()
        .single();

      if (transactionError) throw transactionError;

      // 2. Update account balance ONLY if NOT a pending check
      // For checks: Only update when status = 'cleared'
      // For other methods: Update immediately
      if (input.payment_method !== 'check') {
        // Update account balance for non-check payments
        const { error: balanceError } = await supabase.rpc('update_account_balance', {
          p_account_id: input.account_id,
          p_amount: input.amount,
          p_operation: 'add',
        });

        if (balanceError) {
          console.error('Balance update error:', balanceError);
          // Don't throw - transaction already created
        }
      }
      // Note: For checks, balance will be updated when check is cleared via useClearCheck

      // 2. Create the invoice_payment link
      const { data: payment, error: paymentError } = await supabase
        .from('invoice_payments')
        .insert({
          company_id: input.companyId,
          invoice_id: input.invoice_id,
          transaction_id: transaction.id,
          amount_allocated: input.amount,
          payment_date: input.payment_date,
          payment_method: input.payment_method,
          transaction_number: transaction.transaction_number,
          check_number: input.check_number,
          notes: input.notes,
          created_by: input.userId,
        })
        .select()
        .single();

      if (paymentError) throw paymentError;

      return { transaction, payment };
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', variables.invoice_id] });
      queryClient.invalidateQueries({ queryKey: ['invoice-with-payments', variables.invoice_id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['outstanding-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['ar-aging-stats'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Delete a payment
 */
export function useDeletePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paymentId, invoiceId }: { paymentId: string; invoiceId: string }) => {
      // Soft delete
      const { error } = await supabase
        .from('invoice_payments')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', paymentId);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoice-with-payments', variables.invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['outstanding-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['ar-aging-stats'] });
    },
  });
}
